import { useState } from 'react';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Trash2, AlertTriangle } from 'lucide-react';

// Danger-zone "Delete my account" card (Apple 5.1.1(v) / Google Play
// compliance). Anonymizes the account server-side, then signs the user out.
// Password accounts must re-enter their password; guest / OAuth-only accounts
// just confirm by typing DELETE.
export const DeleteAccountCard = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const needsPassword = !!user?.has_password && !user?.is_guest;
  const canSubmit =
    confirmText.trim().toUpperCase() === 'DELETE' &&
    (!needsPassword || pwd.length > 0) &&
    !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await authAPI.deleteAccount({
        current_password: needsPassword ? pwd : undefined,
        confirm: true,
      });
      toast.success(data?.message || 'Your account has been deleted.');
      setPwd(''); setConfirmText('');
      setTimeout(() => logout(), 1200);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not delete your account. Please try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-card border border-destructive/40 rounded-2xl p-4 mb-4"
      data-testid="delete-account-card"
    >
      <h3 className="text-sm font-semibold mb-1 flex items-center gap-2 text-destructive">
        <AlertTriangle className="h-4 w-4" /> Delete account
      </h3>
      <p className="text-[11px] text-muted-foreground mb-3">
        Permanently removes your personal data (email, username, login). This can&apos;t be undone.
      </p>
      {!open ? (
        <Button
          variant="outline"
          size="sm"
          className="border-destructive/50 text-destructive hover:bg-destructive/10"
          onClick={() => setOpen(true)}
          data-testid="delete-account-open-btn"
        >
          <Trash2 className="h-4 w-4 mr-1.5" /> Delete my account
        </Button>
      ) : (
        <form onSubmit={submit} className="space-y-2">
          {needsPassword && (
            <Input
              type="password"
              placeholder="Your password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              autoComplete="current-password"
              data-testid="delete-account-password"
            />
          )}
          <Input
            type="text"
            placeholder='Type DELETE to confirm'
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            data-testid="delete-account-confirm-text"
          />
          <p className="text-[10px] text-muted-foreground">
            Your teams stay on league leaderboards but show as &quot;Deleted User&quot;.
          </p>
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              variant="destructive"
              disabled={!canSubmit}
              data-testid="delete-account-submit"
            >
              {busy ? 'Deleting…' : 'Permanently delete'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => { setOpen(false); setPwd(''); setConfirmText(''); }}
              disabled={busy}
              data-testid="delete-account-cancel"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
